import React, { useContext, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "../admin.css";
import {
  faMagnifyingGlass,
  faUsers,
  faUserSlash
} from "@fortawesome/free-solid-svg-icons";
import AddOrganisation from "../../../Modals/Admin/AddOrganisationModal";
import SearchComponent from "../../../CommonComponents/SearchComponent/SearchComponent";
import Breadcrumb from "../../../CommonComponents/BreadCrumb/BreadCrumb";
import { networkService } from "../../../config/axios";
import ManageDomain from "../../../Modals/Admin/ManageDomainModal";
import ManagePayment from "../../../Modals/Admin/ManagePaymentModal";
import CustomTable from "../../../CommonComponents/Table/CustomTable";
import CustomTooltip from "../../../Elements/Tooltip";
import DeleteOrgModal from "../../../Modals/Admin/DeleteOrgModal";
import OrgUsersModal from "../../../Modals/Admin/OrgUsersModal";
import OrgBannedUserModal from "../../../Modals/Admin/OrgBannedUserModal";
import AdminPagination from "../../../CommonComponents/Pagination/AdminPagination";
import { sanitizeHtml } from "../../../../utils/sanitizeHtml";
import DataPerPage from "../../../CommonComponents/Pagination/DataPerPage";
import Button from "../../../Elements/Button";
import UserInfoContext from "../../../../utils/userInfoContext";
import OrganizationDomainsPage from "../../../Modals/Admin/OrganizationDomainsPage";

const Organizations = () => {
  const { setShowNotifications, userInfoData } = useContext(UserInfoContext);
  const [orgs, setOrgs] = useState([]);
  const [openModals, setOpenModals] = useState(null);
  const [orgData, setOrgData] = useState({});
  const [currentPageData, setCurrentPageData] = useState([]);
  const [dataPerPage, setDataPerPage] = useState(10);
  const totalData = orgs?.length;

  const handlePageChange = (pageNumber) => {
    const indexOfLastData = pageNumber * dataPerPage;
    const indexOfFirstData = indexOfLastData - dataPerPage;
    setCurrentPageData(orgs?.slice(indexOfFirstData, indexOfLastData));
  };

  const fetchOrgs = async () => {
    const response = await networkService("admin/orgs.json", "GET");
    setOrgs([...response.data]);
  };

  useEffect(() => {
    fetchOrgs();
  }, [openModals]);

  useEffect(() => {
    handlePageChange(1);
  }, [orgs, dataPerPage]);

  const handleDeleteOrgById = (id) => {
    const url = `admin/orgs/${id}.json`;
    networkService(url, "DELETE")
      .then((response) => {
        if (response.status == 204) {
          setOpenModals(null);
          setOrgData({});
          fetchOrgs();
          setShowNotifications({
            showToast: true,
            toastType: "success",
            toastMessage: "Organization deleted",
          });
        }
      })
      .catch((err) => {
        console.error(err);
        setShowNotifications({
          showToast: true,
          toastType: "danger",
          toastMessage: "Something went wrong"
        });
      });
  };

  const handleDeleteOrg = () => {
    handleDeleteOrgById(orgData?.id);
  };

  const handleDeleteIconClick = (org) => {
    setOrgData(org);
    if ((org.docs_count ?? 0) === 0 && (org.num_users ?? 0) === 0) {
      handleDeleteOrgById(org.id);
    } else {
      setOpenModals("delete");
    }
  };

  // Helper component for info icon with tooltip
  const InfoTooltip = ({ content }) => (
    <CustomTooltip
      title={<div className="p-2" dangerouslySetInnerHTML={{ __html: sanitizeHtml(content ?? "") }} />}
   
      placement="top"
    >
      <img src="/icons/info-color.svg" alt="" className="" aria-label="Information" />
    </CustomTooltip>
  );

  // Helper component for email icon with tooltip
  const EmailTooltip = ({ content }) => (
    <CustomTooltip
      title={<div className="p-2" dangerouslySetInnerHTML={{ __html: sanitizeHtml(content ?? "") }} />}
      placement="top"
    >
      <img src="/icons/mail.svg" alt="" className="" aria-label="Email domains" />
    </CustomTooltip>
  );

  // Helper component for action icons with tooltips
  const ActionIcon = ({ icon, iconSrc, tooltip, onClick, className = "", ariaLabel }) => (
    <CustomTooltip title={<p className="p-1 text-light">{tooltip}</p>} placement="top">
      {iconSrc ? (
        <img
          src={iconSrc}
          alt=""
          className={`me-2 cursor-pointer admin-org-action-icon ${className}`}
          onClick={onClick}
          aria-label={ariaLabel || tooltip}
          role="button"
        />
      ) : (
        <FontAwesomeIcon
          icon={icon}
          className={`me-2 cursor-pointer ${className}`}
          onClick={onClick}
          aria-label={ariaLabel || tooltip}
        />
      )}
    </CustomTooltip>
  );

  const columns = React.useMemo(() => {
    return [
      {
        Header: "Name",
        accessor: "name",
        canSort: true,
        Cell: ({ row }) => (
          <div className="admin-org-name">
            <span className="admin-org-name-link">{row.original?.name}</span>
            <InfoTooltip content={row?.original?.creator_modifier} />
          </div>
        ),
      },
      {
        Header: "Type",
        accessor: "type_name",
        canSort: true,
        Cell: ({ row }) => (
          <div className="admin-org-type">
            <CustomTooltip
              title={<div className="p-2" dangerouslySetInnerHTML={{ __html: sanitizeHtml(row?.original?.info ?? "") }} />}
              placement="top"
            >
              <span>{row.original.type_name}</span>
            </CustomTooltip>
            {/* <InfoTooltip content={row?.original?.info} /> */}
            {/* <EmailTooltip content={row?.original?.domains_text} /> */}
          </div>
        ),
      },
      {
        Header: "Trial",
        accessor: "trial",
        canSort: true,
        Cell: ({ row }) => (
          <span className="admin-org-trial">{row.original.trial} Days</span>
        ),
      },
      {
        Header: "Last Login",
        accessor: "last_signed_username",
        canSort: true,
      },
      {
        Header: "Date Time",
        accessor: "last_signed_time",
        canSort: true,
        Cell: ({ row }) => {
          const raw = row.original.last_signed_time;
          if (!raw) return null;
          const [date, time] = raw.split(' @ ');
          return time ? <>{date} @<br />{time}</> : raw;
        },
      },
      {
        Header: "Users",
        accessor: "num_online_users",
        canSort: true,
        Cell: ({ row }) => (
          <div
            onClick={() => {
              setOpenModals("userManagement");
              setOrgData(row.original);
            }}
            className="hov-clr cursor-pointer admin-org-users"
            role="button"
            tabIndex={0}
            aria-label={`View users for ${row.original.name}`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setOpenModals("userManagement");
                setOrgData(row.original);
              }
            }}
          >
            {/* <FontAwesomeIcon icon={faUsers} className="me-2" aria-hidden="true" /> */}
            <span>
              {row.original.num_users} User{row.original.num_users > 1 ? "s" : ""}
            </span>
            {/* {row.original.num_online_users > 0 && (
              <span>
                {row.original.num_online_users} User
                {row.original.num_online_users > 1 ? "s" : ""} Online
              </span>
            )} */}
            {/* {row?.original?.num_online_users > 0 && (
              <FontAwesomeIcon icon={faCircle} className="ms-2 user-active" />
            )} */}
          </div>
        ),
      },
      {
        Header: "Banned Users",
        accessor: "num_banned_users",
        canSort: true,
        Cell: ({ row }) => (
          <div
            onClick={() => {
              setOpenModals("bannedUser");
              setOrgData(row.original);
            }}
            className="hov-clr cursor-pointer admin-org-banned admin-org-banned-cell"
            role="button"
            tabIndex={0}
            aria-label={`View banned users for ${row.original.name}`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setOpenModals("bannedUser");
                setOrgData(row.original);
              }
            }}
          >
            {/* <FontAwesomeIcon icon={faUserSlash} className="me-2" aria-hidden="true" /> */}
            <span>{row.original.num_banned_users}</span>
          </div>
        ),
      },
      {
        Header: "Actions",
        accessor: "action",
        Cell: ({ row }) => (
          <div className="org-icon hov-clr d-flex admin-org-actions">
            <ActionIcon
              iconSrc="/icons/edit.svg"
              tooltip="Edit"
              className="success success-green"
              onClick={() => {
                setOrgData(row?.original);
                setOpenModals("org");
              }}
              ariaLabel={`Edit ${row.original.name}`}
            />
            <CustomTooltip
              title={
                row?.original?.domains_text ? (
                  <div className="p-2" dangerouslySetInnerHTML={{ __html: sanitizeHtml(row.original.domains_text) }} />
                ) : (
                  <p className="p-1 text-light">Email Domains</p>
                )
              }
              placement="top"
            >
              <img
                src="/icons/mail.svg"
                alt=""
                className="me-2 cursor-pointer admin-org-action-icon"
                onClick={() => {
                  setOpenModals("domain");
                  setOrgData(row?.original);
                }}
                aria-label={`Manage email domains for ${row.original.name}`}
                role="button"
              />
            </CustomTooltip>
            <ActionIcon
              iconSrc="/icons/local_atm.svg"
              tooltip="Payment"
              onClick={() => {
                setOpenModals("payment");
                setOrgData(row?.original);
              }}
              ariaLabel={`Manage payment for ${row.original.name}`}
            />
            <ActionIcon
              iconSrc="/icons/globe.svg"
              tooltip="Domains"
              onClick={() => {
                setOpenModals("domainManager");
                setOrgData(row?.original);
              }}
              ariaLabel={`Manage domains for ${row.original.name}`}
            />
            {userInfoData?.company?.id !== row?.original?.id && (
              <ActionIcon
                iconSrc="/icons/delete.svg"
                tooltip="Delete"
                className="cancel"
                onClick={() => handleDeleteIconClick(row?.original)}
                ariaLabel={`Delete ${row.original.name}`}
              />
            )}
          </div>
        ),
      },
    ];
  }, [currentPageData]);

  const handleCloseModal = () => {
    setOpenModals(null);
    setOrgData({});
  };

  const OrgModals = () => {
    switch (openModals) {
      case "org":
        return (
          <AddOrganisation
            openModal={openModals === "org"}
            closeModal={handleCloseModal}
            orgData={orgData}
            setOrgData={setOrgData}
          />
        );
      case "bannedUser":
        return (
          <OrgBannedUserModal
            openModal={openModals === "bannedUser"}
            closeModal={handleCloseModal}
            companyId={orgData?.id}
            title={orgData?.name}
            type="bannedUser"
          />
        );
      case "domain":
        return (
          <ManageDomain
            openModal={openModals === "domain"}
            closeModal={handleCloseModal}
            companyId={orgData?.id}
          />
        );
      case "payment":
        return (
          <ManagePayment
            openModal={openModals === "payment"}
            closeModal={handleCloseModal}
            companyId={orgData?.id}
          />
        );
      case "delete":
        return (
          <DeleteOrgModal
            title="Delete Organization"
            openModal={openModals === "delete"}
            closeModal={handleCloseModal}
            name={"delete " + orgData?.name}
            warning="All data including LaserLINX, Stats and KDFs will be permanently deleted and can not be undone."
            handleDelete={handleDeleteOrg}
          />
        );
      case "userManagement":
        return (
          <OrgUsersModal
            openModal={openModals === "userManagement"}
            closeModal={handleCloseModal}
            companyId={orgData?.id}
            title={orgData?.name}
            type="userManagement"
          />
        );
      case "domainManager":
        return (
          <OrganizationDomainsPage
            openModal={openModals === "domainManager"}
            closeModal={handleCloseModal}
            company={orgData}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {OrgModals()}

      <div className="admin-module">
      <div className="admin-org-page admin-content">
      <Breadcrumb string="Admin / Organisations" />

      <div className="container admin-org-container">
        <div className="mb-4 show-select1 d-flex admin-org-header">
          <div className="admin-org-controls admin-org-search-group">
            {totalData > dataPerPage && <DataPerPage dataPerPage={dataPerPage} setDataPerPage={setDataPerPage} />}
            <div className="admin-org-search-input-wrap">
              <SearchComponent
                data={orgs}
                setCurrentPageData={setCurrentPageData}
                placeholder="Search here"
              />
            </div>
            <button
              type="button"
              className="admin-org-search-btn"
              aria-label="Search"
            >
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </button>
          </div>

          <Button
            classList="success"
            addonClass="admin-org-cta"
            text={<>+ Add Organisation</>}
            onClick={() => {
              setOpenModals("org");
            }}
            type="button"
            aria-label="Add new organization"
          />
        </div>

        <div className="top-parent admin-org-table-wrapper">
          <CustomTable data={currentPageData} columnData={columns} />
        </div>

        {totalData > dataPerPage && (
          <AdminPagination
            dataPerPage={dataPerPage}
            totalData={totalData}
            onPageChange={handlePageChange}
            showRange={true}
            previousLabel="‹ Previous"
            nextLabel="Next ›"
          />
        )}
      </div>
    </div>
    </div>
    </>
  );
};

export default Organizations;
